import { z } from "zod";
import {
  completionItemSchema,
  completionSelectionSchema,
} from "./completion-picker";
import type { MedicalEvidenceTaskKey } from "./medical-evidence";
import {
  WEBDOC_LAB_ORDER_UNCONFIRMED,
  webdocControlSchema,
  webdocLabOrderConfirmed,
} from "./prescription-choice";

// TES-1169 (Emelie 2026-10-08, beslut 2) — "Begär komplettering" from the
// patient overview may be used "under vårdmötet, direkt efter, eller senare
// när det finns ett aktivt medicinskt patientärende". This file is the ONE
// definition of that case: the server refuses a request without it, and the
// admin shows the button disabled with the reason it returns.
//
// Her definition, verbatim (TES-1169):
//   "Aktivt medicinskt patientärende (minst ett):
//    medicinsk bedömning pågår; läkaren inväntar underlag inför beslut;
//    positivt beslut finns men behandlingsstart/ordination behöver
//    kompletteras; pågående behandling hos Ella; behandlingsplan,
//    månadsuppföljning eller receptförnyelse under medicinsk bedömning."
//   "Konto/abonnemang ensamt är inte aktivt ärende. Inte aktivt efter
//    slutligt avslag och avslut, uttryckligt avbrott, avslutad behandling och
//    vårdrelation, eller administrativ stängning utan pågående uppföljning."
//
// The predicate is pure over stored facts the server reads in one place
// (`activeMedicalCaseFacts` in apps/api), so every clause is unit-tested here.

// TES-1200 (Ö-403, Emelie 2026-10-08) widened it: "Ett medicinskt ärende är
// aktivt från att patienten har påbörjat den aktuella vårdprocessen tills den
// får ett dokumenterat slutligt utfall." So a case ends ONLY on a documented
// final outcome. Since TES-1555 that is: the doctor's documented ended care
// relationship (PatientCareEnding), a rejection already on record (an
// assessment outcome), or the account's deletion (an account fact that
// leaves nothing to treat). "Ett pausat ärende är fortfarande aktivt men har
// status Pausat. Inaktivitet, uteblivet svar eller avsaknad av aktivt
// abonnemang ska inte ensamt stänga det medicinska ärendet."

// The five clauses, in her order, plus TES-1200's: a treatment that stopped
// without a documented final outcome (the past_due grace expiry, say) keeps
// the case open. A case is active when at least one holds.
export const ACTIVE_MEDICAL_CASE_CLAUSES = [
  "assessment_in_progress",
  "awaiting_evidence",
  "positive_decision_pending_start",
  "ongoing_treatment",
  "follow_up_under_review",
  "no_final_outcome",
] as const;
export const activeMedicalCaseClauseSchema = z.enum(
  ACTIVE_MEDICAL_CASE_CLAUSES,
);
export type ActiveMedicalCaseClause = z.infer<
  typeof activeMedicalCaseClauseSchema
>;

// TES-1555 S2 (Ö-411, Emelie 2026-10-21) — the one documented ending of a
// medical case: an ended care relationship, with its reason. "Avslutad
// vårdrelation dokumenteras separat med anledning, exempelvis ”Patienten
// lämnar Ella”, ”Vården överförs” eller ”Ella avslutar vårdrelationen”."
// "Ingen generell patientstatus Slutligt stängt medicinskt ärende."
//
// What is NOT an ending (Ö-411, Ö-429):
//   - a finished task (a reviewed check-in, an issued prescription, a closed
//     queue card): the treatment goes on;
//   - a treatment pause: "fortsatt under Ellas vård enligt befintligt flöde";
//   - the doctor ending the drug treatment (TES-1555 S1);
//   - a rejected application: "bedömningsutfall, inte avslut av pågående
//     behandling" — its decision record already says so;
//   - an administrative closure or the account's deletion: "separata
//     handlingar och fattar inte automatiskt medicinska beslut".
export const CARE_RELATIONSHIP_ENDING_REASONS = [
  "patient_leaves_ella",
  "care_transferred",
  "ella_ends_relationship",
] as const;
export const careRelationshipEndingReasonSchema = z.enum(
  CARE_RELATIONSHIP_ENDING_REASONS,
);
export type CareRelationshipEndingReason = z.infer<
  typeof careRelationshipEndingReasonSchema
>;

// Emelie's examples, verbatim (Ö-411).
export const CARE_RELATIONSHIP_ENDING_REASON_LABEL: Record<
  CareRelationshipEndingReason,
  string
> = {
  patient_leaves_ella: "Patienten lämnar Ella",
  care_transferred: "Vården överförs",
  ella_ends_relationship: "Ella avslutar vårdrelationen",
};

// TES-1200's "final outcomes", recorded before TES-1555 in
// medical_case_outcomes. Read-only history now: the table refuses new rows.
// The ones that were an ended care relationship were copied into
// PatientCareEnding (migration 20261026100000); the others have no
// effect any more.
export const LEGACY_MEDICAL_CASE_OUTCOMES = [
  "patient_withdrew",
  "care_relationship_ended",
  "final_rejection",
  "discharged_or_transferred",
  "administrative_closure",
] as const;
export const legacyMedicalCaseOutcomeSchema = z.enum(
  LEGACY_MEDICAL_CASE_OUTCOMES,
);
export type LegacyMedicalCaseOutcome = z.infer<
  typeof legacyMedicalCaseOutcomeSchema
>;

// The labels they were recorded under (TES-1200), kept so the history reads
// as it was written.
export const LEGACY_MEDICAL_CASE_OUTCOME_LABEL: Record<
  LegacyMedicalCaseOutcome,
  string
> = {
  patient_withdrew: "Patienten har avbrutit vården",
  care_relationship_ended: "Ella har avslutat vårdrelationen",
  final_rejection: "Slutligt avslag",
  discharged_or_transferred: "Utskriven eller vården överförd",
  administrative_closure: "Administrativt stängt",
};

// Why there is no active case:
//   care_relationship_ended  a standing PatientCareEnding;
//   assessment_rejected      the assessment ended in a rejection on record
//                            (CP-09 declined, cannot_transfer, a rejected
//                            cycle): an assessment outcome, not an ending;
//   account_deleted          an account fact (Patient.anonymisedAt), not a
//                            medical decision;
//   no_medical_case          "Konto/abonnemang ensamt": nothing medical has
//                            started.
export const INACTIVE_MEDICAL_CASE_REASONS = [
  "care_relationship_ended",
  "assessment_rejected",
  "account_deleted",
  "no_medical_case",
] as const;
export const inactiveMedicalCaseReasonSchema = z.enum(
  INACTIVE_MEDICAL_CASE_REASONS,
);
export type InactiveMedicalCaseReason = z.infer<
  typeof inactiveMedicalCaseReasonSchema
>;

// Staff-facing (the doctor reads them beside the button). Proposed copy,
// derived from her definition; the closing sentence of each exclusion is hers
// (A-02, 2026-10-08: "öppnas först ett nytt medicinskt ärende eller en ny
// uppföljning").
export const ACTIVE_MEDICAL_CASE_CLAUSE_LABEL: Record<
  ActiveMedicalCaseClause,
  string
> = {
  assessment_in_progress: "Medicinsk bedömning pågår",
  awaiting_evidence: "Läkaren inväntar underlag inför beslut",
  positive_decision_pending_start:
    "Positivt beslut finns – behandlingsstart eller ordination återstår",
  ongoing_treatment: "Pågående behandling hos Ella",
  follow_up_under_review:
    "Månadsuppföljning eller receptförnyelse under medicinsk bedömning",
  no_final_outcome:
    "Behandlingen har upphört utan ett dokumenterat slutligt utfall",
};

const OPEN_NEW_CASE_FIRST =
  "Öppna ett nytt medicinskt ärende eller en ny uppföljning först.";

// Staff-facing. The care_relationship_ended and assessment_rejected lines are
// proposed copy, to confirm with Kirill (TES-1555).
export const INACTIVE_MEDICAL_CASE_REASON_LABEL: Record<
  InactiveMedicalCaseReason,
  string
> = {
  care_relationship_ended: `Vårdrelationen är avslutad. ${OPEN_NEW_CASE_FIRST}`,
  assessment_rejected: `Bedömningen ledde till ett slutligt avslag. ${OPEN_NEW_CASE_FIRST}`,
  account_deleted: "Patientens konto är raderat.",
  no_medical_case:
    "Det finns inget aktivt medicinskt ärende. Ett konto eller abonnemang räcker inte.",
};

export interface ActiveMedicalCaseFacts {
  /** Patient.anonymisedAt is set: her account is deleted (Art. 17). */
  accountDeleted: boolean;
  /**
   * The reason of her standing PatientCareEnding (not revoked), or
   * null: the doctor's documented end of the care relationship (TES-1555).
   */
  careRelationshipEnding: CareRelationshipEndingReason | null;
  /** Patient.journeyState (JOURNEY_STATES; a free string on the wire). */
  journeyState: string;
  /** Treatment.state, or null when no Treatment row exists. */
  treatmentState: string | null;
  /** Every PrescriptionCycle.state of her treatment. */
  cycleStates: readonly string[];
  /** The latest PrescriptionTransferReview's status, if any. */
  transferReviewStatus: string | null;
  /**
   * The published first treatment decision (CP-09) is "not approved": the
   * only record a rejection before any cycle leaves behind.
   */
  rejectionPublished: boolean;
  /** The CP-06 checklist (medicalEvidenceState) has an open case. */
  evidenceCaseOpen: boolean;
  /**
   * A positive decision is published: an approved transfer review, or the
   * clinical phase that only a positive decision writes (TES-841).
   */
  positiveDecisionPublished: boolean;
  /** A consult with her is in progress, held or documented. */
  consultStartedOrHeld: boolean;
  /**
   * F-01 has routed her to prescription transfer: her stored patient group (the
   * doctor's override first) is PRESCRIPTION_TRANSFER.
   */
  transferRouted: boolean;
}

export type ActiveMedicalCase =
  | { active: true; clauses: ActiveMedicalCaseClause[]; paused: boolean }
  | { active: false; reason: InactiveMedicalCaseReason };

// Before any decision: she has handed in her health declaration, or a
// doctor is reviewing her transfer, or the consult has been held.
//
// TES-1220 (Ö-432, assumption pending Emelie) — or F-01 has routed her to
// prescription transfer. TES-1200 defines the case as active from when the
// patient started the current care process, and for a transfer that starts at the
// routing: no first meeting lies ahead of her, so nothing later marks the
// start. Without this she could not book the voluntary meeting Emelie's Hem
// card offers her before she submits (careMeetingEligibility).
const ASSESSMENT_JOURNEY = new Set([
  "health_declaration_completed",
  "prescription_transfer_review_pending",
  "doctor_visit_completed",
  "labs_completed",
  "treatment_decision_ready",
]);
const ASSESSMENT_TRANSFER = new Set(["pending_review", "meeting_required"]);
const AWAITING_TRANSFER = new Set(["completion_requested", "labs_required"]);
// Same sets as deriveClinicalStage (patient-header.ts): a live prescription,
// or a journey in the treatment era.
const LIVE_TREATMENT = new Set(["active", "between_cycles", "paused"]);
const TREATMENT_ERA_JOURNEY = new Set([
  "treatment_started",
  "active_treatment",
  "maintenance",
  "paused",
]);
// The renewal / monthly follow-up is with the doctor.
const FOLLOW_UP_REVIEW_CYCLE = new Set(["renewal_pending", "doctor_review"]);

// A rejection a doctor's decision on record documents. An ended treatment
// with none behind it (the past_due grace expiry ends one without a
// decision) has NO outcome, and Ö-403 keeps that case open.
//
// TES-1555 S1 (Ö-411, Ö-429) — nor does the doctor's "Avsluta behandlingen"
// (the treatment-status end, which discontinues the cycles): ending the drug
// treatment is not ending the care relationship, so a discontinued cycle
// derives nothing and the case stays active (clause no_final_outcome).
//
// TES-1555 S2 — and a rejection is "bedömningsutfall, inte avslut av
// pågående behandling": it is read as assessment_rejected, never as an
// ending. Ö-887 (open) asks whether a rejected monthly renewal should be
// read this way too; today it is, as it was before.
function assessmentRejected(f: ActiveMedicalCaseFacts): boolean {
  return (
    f.transferReviewStatus === "cannot_transfer" ||
    f.cycleStates.includes("rejected") ||
    f.rejectionPublished
  );
}

export function activeMedicalCase(
  f: ActiveMedicalCaseFacts,
): ActiveMedicalCase {
  // Endings first: once a case has ended, no clause re-opens it — "nya
  // uppgifter får inte läggas i ett historiskt ärende" (A-02, 2026-10-08).
  if (f.accountDeleted) return { active: false, reason: "account_deleted" };
  // A documented ended care relationship stands until a doctor revokes it
  // (a new care process).
  if (f.careRelationshipEnding) {
    return { active: false, reason: "care_relationship_ended" };
  }
  const inTreatmentEra = TREATMENT_ERA_JOURNEY.has(f.journeyState);
  // `ended` is the journey overlay every ending writes (TreatmentService
  // setState). A returning patient's new application moves the journey off
  // it, so her new case is judged on its own below; the treatment row stays
  // `ended` until a new approval revives it.
  const ended =
    f.journeyState === "ended" ||
    (f.treatmentState === "ended" && inTreatmentEra);
  if (ended && assessmentRejected(f)) {
    return { active: false, reason: "assessment_rejected" };
  }

  const liveTreatment =
    !ended &&
    ((f.treatmentState !== null && LIVE_TREATMENT.has(f.treatmentState)) ||
      inTreatmentEra);
  const clauses: ActiveMedicalCaseClause[] = [];

  if (
    !ended &&
    !f.positiveDecisionPublished &&
    !liveTreatment &&
    (ASSESSMENT_JOURNEY.has(f.journeyState) ||
      (f.journeyState === "meeting_booked" && f.consultStartedOrHeld) ||
      f.transferRouted ||
      (f.transferReviewStatus !== null &&
        ASSESSMENT_TRANSFER.has(f.transferReviewStatus)))
  ) {
    clauses.push("assessment_in_progress");
  }
  if (
    f.evidenceCaseOpen ||
    f.journeyState === "labs_required" ||
    (f.transferReviewStatus !== null &&
      AWAITING_TRANSFER.has(f.transferReviewStatus))
  ) {
    clauses.push("awaiting_evidence");
  }
  if (!ended && f.positiveDecisionPublished && !liveTreatment) {
    clauses.push("positive_decision_pending_start");
  }
  if (liveTreatment) clauses.push("ongoing_treatment");
  if (f.cycleStates.some((s) => FOLLOW_UP_REVIEW_CYCLE.has(s))) {
    clauses.push("follow_up_under_review");
  }
  // The treatment stopped and nothing documents that it ended for good: still
  // her case ("avsaknad av aktivt abonnemang ska inte ensamt stänga").
  if (ended) clauses.push("no_final_outcome");

  if (clauses.length === 0) return { active: false, reason: "no_medical_case" };
  // "Ett pausat ärende är fortfarande aktivt men har status Pausat."
  const paused =
    liveTreatment &&
    (f.treatmentState === "paused" || f.journeyState === "paused");
  return { active: true, clauses, paused };
}
// ---- the wire ----------------------------------------------------------------

// GET employee-api/v1/patients/:id/completion-request — what the admin needs
// to show the button: enabled with the clauses that hold, or disabled with
// the reason.
export const activeMedicalCaseResponseSchema = z.discriminatedUnion("active", [
  z.object({
    active: z.literal(true),
    clauses: z.array(activeMedicalCaseClauseSchema),
    paused: z.boolean(),
  }),
  z.object({
    active: z.literal(false),
    reason: inactiveMedicalCaseReasonSchema,
    message: z.string(),
  }),
]);
export type ActiveMedicalCaseResponse = z.infer<
  typeof activeMedicalCaseResponseSchema
>;

export function activeMedicalCaseResponse(
  c: ActiveMedicalCase,
): ActiveMedicalCaseResponse {
  return c.active
    ? { active: true, clauses: c.clauses, paused: c.paused }
    : {
        active: false,
        reason: c.reason,
        message: INACTIVE_MEDICAL_CASE_REASON_LABEL[c.reason],
      };
}

// POST employee-api/v1/patients/:id/completion-request — the TES-950 picker's
// selection, exactly as the meeting, the transfer review and the monthly
// review send it.
//
// TES-1181 (TES-1180 point 5) — plus the remiss tick when the selection asks
// for a new blood test, as every other route that can ask for one requires:
// "Patienten får inte uppmanas att lämna blodprov innan Kirill har bekräftat att provbeställningen/remissen faktiskt är skapad i Webdoc."
export const overviewCompletionRequestSchema = completionSelectionSchema
  .and(z.object({ webdocControl: webdocControlSchema.optional() }))
  .superRefine((v, ctx) => {
    if (
      v.items.includes("new_blood_test") &&
      !webdocLabOrderConfirmed(v.webdocControl)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["webdocControl", "labOrderDoneInWebdoc"],
        message: WEBDOC_LAB_ORDER_UNCONFIRMED,
      });
    }
  });
export type OverviewCompletionRequest = z.infer<
  typeof overviewCompletionRequestSchema
>;

export interface OverviewCompletionRequestResult {
  requestId: string;
  // The CP-06 case the request now belongs to: the case she was already
  // working on when one was open (the request joins it), else its own.
  caseKind: string;
  joinedOpenCase: boolean;
  items: z.infer<typeof completionItemSchema>[];
  tasks: MedicalEvidenceTaskKey[];
}

// ---- TES-1555: the documented ended care relationship ------------------------

// POST employee-api/v1/patients/:id/medical-case/care-relationship-ending:
// the doctor documents that her care relationship has ended, with its
// reason. The note is optional free text (PHI), kept encrypted and out of
// audit metadata.
export const CARE_RELATIONSHIP_NOTE_MAX = 2000;
export const recordCareRelationshipEndingSchema = z.object({
  reason: careRelationshipEndingReasonSchema,
  note: z.string().trim().max(CARE_RELATIONSHIP_NOTE_MAX).optional(),
});
export type RecordCareRelationshipEnding = z.infer<
  typeof recordCareRelationshipEndingSchema
>;

// One documented ending, newest first in the history. `revokedAt` set = a
// doctor started a new care process after it; the row stays as history.
export const careRelationshipEndingRecordSchema = z.object({
  id: z.string(),
  reason: careRelationshipEndingReasonSchema,
  note: z.string().nullable(),
  recordedAt: z.string(),
  // Null when the employee is unknown, or on a copy of a withdrawal she
  // recorded herself in the app before TES-1555 (legacyOutcomeId names it).
  recordedByName: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedByName: z.string().nullable(),
  // The pre-TES-1555 outcome it was copied from, listed in legacyOutcomes.
  legacyOutcomeId: z.string().nullable(),
});
export type CareRelationshipEndingRecord = z.infer<
  typeof careRelationshipEndingRecordSchema
>;

// A TES-1200 outcome recorded before TES-1555, shown as it was written.
export const legacyMedicalCaseOutcomeRecordSchema = z.object({
  id: z.string(),
  outcome: legacyMedicalCaseOutcomeSchema,
  reason: z.string().nullable(),
  recordedAt: z.string(),
  recordedByName: z.string().nullable(),
  // TES-1203 — she recorded it herself in the app ("Avbryt min vård").
  recordedByPatient: z.boolean(),
  reopenedAt: z.string().nullable(),
  reopenedByName: z.string().nullable(),
});
export type LegacyMedicalCaseOutcomeRecord = z.infer<
  typeof legacyMedicalCaseOutcomeRecordSchema
>;

// GET employee-api/v1/patients/:id/medical-case: the verdict and the history.
export const medicalCaseResponseSchema = z.object({
  verdict: activeMedicalCaseResponseSchema,
  endings: z.array(careRelationshipEndingRecordSchema),
  legacyOutcomes: z.array(legacyMedicalCaseOutcomeRecordSchema),
});
export type MedicalCaseResponse = z.infer<typeof medicalCaseResponseSchema>;
